import { Close } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Box, FormControl, IconButton, InputAdornment, Modal, Paper, Typography } from "@mui/material"
import { Fragment, ReactNode, useRef, useState } from "react"
import { greaterThanValueValidationFn, parseFalsyFields } from '../utils/ValidationUtils';
import { GobikesApi } from "../../config/Apis";
import { getAuthCookie } from "../auth/AuthUtil";
import { OrderModel, OrderUploadLabels } from "../utils/Models";
import { GbkFieldType, GobikesField } from "../utils/GobikesField";
import { UploadImageViewer } from "./UploadDetail";
import { Time } from "../../config/Constants";

// Interfaces to store state
interface BikeRcData {
  rcNumber: string, odStart: number, odEnd?: number
}
interface DistancePenaltyBreakdown {
  rcNumber: string,
  start: number,
  end: number
}
interface FormState {
  loading?: boolean;
  error?: boolean;
  message?: string;
  rcData: BikeRcData[];
  errors: string[];
  latePenalty?: number;
  extraHours?: number;
  distancePenalty?: number;
  extraDistance?: number;
  useCustomPenalty?: boolean;
  distancePenaltyBreakdown?: DistancePenaltyBreakdown[]
}

// Start booking component
export function EndBooking(props: {
  children: ReactNode,
  order: OrderModel,
  style: any,
  backFn: () => void
}) {

  // Refs and constants
  const bikeCount = props.order?.bikes?.[0]?.order_bikes?.quantity || 0;
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const defaultExtraHours = Math.max(0, Math.ceil((new Date().getTime() - props.order.rideEndScheduled) / Time.HOUR));
  const defaultLatePenalty = Number((
    defaultExtraHours
    * (props.order.staticData?.penalty?.late || 0)).toFixed(2)
  );

  // Local state
  const [formState, setFormState] = useState<FormState>({
    rcData: props.order.bikeDetails as BikeRcData[],
    errors: Array(bikeCount).fill(''),
    latePenalty: defaultLatePenalty,
    extraHours: defaultExtraHours,
    distancePenalty: 0,
    extraDistance: 0
  });
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  // Parse state or props
  const authCookie = getAuthCookie();
  const allBikeRcData = props.order.bikeDetails || [];
  const emptyFields = parseFalsyFields([
    ...(Array(bikeCount).fill(0).map((_, i) => ({ name: `Odometer reading ${i + 1}`, value: formState?.rcData?.[i].odEnd })))
  ]);
  let disableSubmit = false;
  if (emptyFields.length || formState.errors.find(a => !!a)) {
    disableSubmit = true;
  }

  // Handle  form submission
  async function submitEndOrderRequest(e: any) {

    // Parse input and check for errors
    e.preventDefault();
    if (emptyFields.length || formState.errors.find(a => !!a)) {
      setFormState({ ...formState, error: true, loading: false, message: 'Please fix all errors' });
    }

    // Set loading mode and try to end the booking
    setFormState({ ...formState, loading: true });
    const payload = {
      bikeData: formState.rcData,
      penalties: [
        { type: 'distance', factor: formState.extraDistance, amount: formState.distancePenalty },
        { type: 'late', factor: formState.extraHours, amount: formState.latePenalty }
      ]
    };
    try {
      await GobikesApi.put(`/vendors/hubs/${props.order.hubId}/orders/${props.order.id}/end`, payload, { headers: { 'vendor-token': authCookie } });
      setFormState({ ...formState, error: false, message: 'Ride ended successfully!', loading: false });
      setTimeout(() => { props.backFn() }, 2000);
    } catch (e: any) {
      const errorMessage = (e.response && e.response.data && e.response.data.message) ? 'Error: ' + e.response.data.message : 'An error occured';
      setFormState({ ...formState, error: true, message: errorMessage, loading: false })
    }
  }

  // Return JSX
  return (
    <Fragment>
      <span onClick={handleOpen} style={props.style}>
        {props.children}
      </span>
      <Modal
        open={open}
        className="gbk-modal"
        onClose={handleClose}
      >
        <Box className="gbk-modal-content">
          {renderStartOrderForm()}
        </Box>
      </Modal >
    </Fragment>
  )

  // Render start order form
  function renderStartOrderForm() {
    return <Paper className='gbk-paper'>

      {/* Header */}
      <div className="header">
        <Typography className="header-left"><b>Drop Off</b></Typography>
        <IconButton className="header-right" onClick={handleClose} style={{ padding: 0 }}>
          <Close />
        </IconButton>
      </div>

      {/* Forms */}
      <div className="content">
        <FormControl sx={{ margin: { xs: '10px 20px', md: '20px 30px' }, width: '80%' }} >

          {/* OD readings */}
          {allBikeRcData.map((data, i) => (
            <div key={i} className="gbk-col" style={{ width: 'calc(100% - 10px)' }}>
              <GobikesField
                type={GbkFieldType.NUMBER}
                label={`End Odometer Reading for ${data.rcNumber}`}
                editMode={true}
                useInternalLabel={true}
                value={formState?.rcData?.[i].odEnd || ''}
                validateFn={greaterThanValueValidationFn(data.odStart || 0, `Should be more than odometer start (${data.odStart || 0})`)}
                updateFn={(v: number, error?: string) => {
                  // Calculate distance penalty
                  const { extraDistance, distancePenalty, breakdown } = calculateDefaultDistancePenalty(i, v, formState.useCustomPenalty || false);
                  // Update state
                  const newRcData = formState.rcData || [];
                  newRcData[i].odEnd = v;
                  const newErrors = formState.errors;
                  newErrors[i] = error || '';
                  const newFormState: FormState = { ...formState, rcData: newRcData, errors: newErrors, ...(!formState.useCustomPenalty && { extraDistance, distancePenalty, distancePenaltyBreakdown: breakdown }) }
                  setFormState(newFormState);
                }}
              />
            </div>
          ))}
          <br />

          {/* Drop off process */}
          <Typography sx={{ fontSize: '16px', paddingBottom: '6px' }}><b>Drop Off Process</b></Typography>
          <div style={{ paddingRight: '10px' }}>
            <ul className="roboto gbk-list" style={{ marginTop: '8px' }}>
              <li>Check for any damage and challan</li>
              <li>Calculate the deductions (if any) {props.order.depositAmount > Number.EPSILON ? `and return security deposit (₹ ${props.order.depositAmount})` : ''}</li>
              <li>Take helmet and documents back</li>
            </ul>
          </div>
          <br />

          {/* Uploaded images */}
          {
            props.order.imageData && Object.keys(props.order.imageData).length
              ? (
                <Fragment>
                  <Typography sx={{ fontSize: '16px', paddingBottom: '6px' }}><b>Pickup photos</b></Typography>
                  <br />
                  <div className="gbk-col" style={{ minWidth: 300 }} >
                    {Object.keys(props.order?.imageData as object).map(label => (
                      props.order.imageData && props.order.imageData[label as OrderUploadLabels] ? (
                        <UploadImageViewer
                          key={label}
                          label={label as OrderUploadLabels}
                          awsKey={props.order.imageData[label as OrderUploadLabels] || 'No label'}
                          customLabel={props.order.extraUploadLabels?.[label as OrderUploadLabels.EXTRA_1 | OrderUploadLabels.EXTRA_2]}
                        />
                      ) : ''
                    ))}
                  </div>
                </Fragment>
              )
              : ''
          }

          {/* Penalty */}
          <Typography sx={{ fontSize: '16px', paddingBottom: '6px' }}><b>Penalty</b></Typography>
          <GobikesField
            type={GbkFieldType.CHECKBOX}
            label={'Use custom penalty'}
            editMode={true}
            value={formState.useCustomPenalty || false}
            updateFn={(v) => {
              const newState = Boolean(v);
              const { extraDistance, distancePenalty, breakdown } = calculateDefaultDistancePenalty(-1, -1, newState);
              setFormState({ ...formState, useCustomPenalty: newState, ...(newState === false && { latePenalty: defaultLatePenalty, extraHours: defaultExtraHours, distancePenalty, extraDistance, distancePenaltyBreakdown: breakdown }) })
            }}
          />
          <br />
          <div className="gbk-col" style={{ width: 'calc(100% - 10px)' }}>
            {/* Distance penalty breakdown */}
            {formState.distancePenaltyBreakdown && formState.distancePenaltyBreakdown.length ? (
              <div className="grey-text-dark roboto">
                Distance breakdown:
                {(formState.distancePenaltyBreakdown || []).map((item, i) => (
                  <div style={{ padding: '10px 0' }} key={i}>
                    <span style={{ width: '100px', display: 'inline-block' }}><b>{item.rcNumber}:</b>  </span> <br />
                    <span style={{ width: '200px', display: 'inline-block' }}>End reading: {item.end} Km</span><br />
                    <span style={{ width: '200px', display: 'inline-block', borderBottom: '1px solid #ddd', paddingBottom: '2px' }}>Start reading: {item.start} Km </span> <br />
                    {/* <span style={{ width: '200px', display: 'inline-block', height: '1px', borderBottom: '1px solid #ddd' }}> </span> <br /> */}
                    <span style={{ width: '200px', display: 'inline-block', paddingTop: '2px' }}>Distance driven: {item.end - item.start} Km</span>
                  </div>
                ))}
                <div style={{ padding: '10px 0' }}>
                  <span style={{ display: 'inline-block' }}>
                    <b>Total Km driven: </b>
                    {(formState.distancePenaltyBreakdown || []).map(item => item.end - item.start).reduce((a, b) => a + b, 0)} Km
                  </span> <br />
                  <span style={{ display: 'inline-block', borderBottom: '1px solid #ddd', paddingBottom: '2px' }}>
                    <b>Km limit: </b>
                    {(props.order.staticData?.distanceLimit || 0) < 0 ? 'No limit' : (props.order.staticData?.distanceLimit || 0) + ' Km'}
                  </span> <br />
                  <span style={{ display: 'inline-block', paddingTop: '2px' }}>
                    <b>Extra Km: </b>
                    {formState.extraDistance || 0} Km
                  </span> <br /> <br />
                </div>
              </div>
            ) : ''}
            {/* Distance penalty */}
            <GobikesField
              type={formState.useCustomPenalty ? GbkFieldType.NUMBER : GbkFieldType.STRING}
              label={`Distance penalty`}
              editMode={true}
              disabled={!(formState.useCustomPenalty || false)}
              useInternalLabel={true}
              value={
                formState.useCustomPenalty
                  ? (formState?.distancePenalty || 0)
                  : `${formState?.distancePenalty || 0} (₹${props.order.staticData?.penalty?.distance || 0} x ${formState?.extraDistance} Km)`
              }
              inputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              updateFn={(v: number, error?: string) => {
                setFormState({ ...formState, extraDistance: 0, distancePenalty: v })
              }}
            />
          </div>
          <div className="gbk-col" style={{ width: 'calc(100% - 10px)' }}>
            <GobikesField
              type={formState.useCustomPenalty ? GbkFieldType.NUMBER : GbkFieldType.STRING}
              label={`Late penalty`}
              editMode={true}
              disabled={!(formState.useCustomPenalty || false)}
              useInternalLabel={true}
              value={
                formState.useCustomPenalty
                  ? (formState?.latePenalty || 0)
                  : `${formState?.latePenalty || 0} (₹${props.order.staticData?.penalty?.late || 0} x ${formState?.extraHours} hours)`
              }
              inputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              updateFn={(v: number, error?: string) => {
                setFormState({ ...formState, extraHours: 0, latePenalty: v })
              }}
            />
          </div>


          {/* Send review */}
          {/* <div style={{ width: 200 }}>
            <Button variant="outlined" size="large" className="thick-border" >
              Send Review Link
            </Button>
          </div> */}

          {/* Message */}
          <div className={`gbk-login-message ${formState.error ? 'error' : ''}`}>{formState.message}</div>
        </FormControl>
      </div>

      {/* Footer */}
      <div className="footer">
        <form onSubmit={submitEndOrderRequest}>
          <LoadingButton
            variant='contained'
            type='submit'
            ref={submitButtonRef}
            loading={formState.loading}
            className="no-text-transform"
            sx={{ width: 'calc(100% - 20px)', ml: '10px' }}
            disabled={disableSubmit}
          >
            End Ride
          </LoadingButton>
        </form>
      </div>
    </Paper>
  }

  // Utility function to calculate distance penalty
  function calculateDefaultDistancePenalty(overrideIndex: number, overrideValue: number, customPenaltyOverride: boolean): {
    distancePenalty: number,
    extraDistance: number,
    breakdown: DistancePenaltyBreakdown[]
  } {
    let distancePenalty = 0;
    let extraDistance = 0;
    const dpBreakdown: DistancePenaltyBreakdown[] = [];
    const customPenalty = customPenaltyOverride;
    let totalDistance = 0;
    // calculation needed
    if (props.order.staticData && !customPenalty) {
      // current bike calculation
      let odStart = 0;
      let odEnd = 0;
      let currentBikeObject;
      for (let j = 0; j < formState.rcData.length; j++) {
        odEnd = overrideIndex === j ? overrideValue : formState.rcData[j].odEnd || 0;
        if (odEnd && allBikeRcData && allBikeRcData[j]) {
          odStart = allBikeRcData[j].odStart || 0;
          if (odStart && !isNaN(odStart) && odStart > -1 && odStart < odEnd) {
            currentBikeObject = { start: odStart, end: odEnd, rcNumber: allBikeRcData[j].rcNumber };
            // dpBreakdown.push({ start: odStart, end: odEnd, rcNumber: allBikeRcData[j].rcNumber })
            totalDistance += odEnd - odStart;
          }
        }
      }
      // calculation for old bikes
      if (props.order.bikeUpdateDetails && !customPenalty) {
        for (const bikeUpdate of props.order.bikeUpdateDetails) {
          for (const bike of (bikeUpdate.bikeDetails || [])) {
            if (bike.odStart && bike.odEnd && !isNaN(bike.odStart) && !isNaN(bike.odEnd) && odStart > -1 && odStart < odEnd) {
              dpBreakdown.push({ start: bike.odStart, end: bike.odEnd, rcNumber: bike.rcNumber })
              totalDistance += bike.odEnd - bike.odStart;
            }
          }
        }
      }
      // add current bike object
      if (currentBikeObject) {
        dpBreakdown.push(currentBikeObject);
      }
      // final penalty calculation
      if (props.order.staticData.distanceLimit > 0) {
        extraDistance = Math.max(totalDistance - props.order.staticData.distanceLimit, 0);
        distancePenalty = Number((extraDistance * (props.order.staticData?.penalty?.distance || 0)).toFixed(2));
      }
    }
    return { distancePenalty, extraDistance, breakdown: dpBreakdown }
  }
}

